/**
 * Domain creation form component with validation.
 */

import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import type { DomainCreate } from '@/api/schemas';
import './domain-form.scss';

const domainSchema = z.object({
  domain_name: z
    .string()
    .min(3, 'Domain name must be at least 3 characters')
    .max(255, 'Domain name must be less than 255 characters')
    .regex(
      /^([a-z0-9]+(-[a-z0-9]+)*\.)+[a-z]{2,}$/i,
      'Please enter a valid domain name'
    )
    .refine(
      (domain) => {
        // Must be a subdomain (not apex/root domain)
        const parts = domain.split('.');
        return parts.length >= 3; // e.g., go.example.com has 3 parts
      },
      {
        message: "Subdomains only. Use 'go.yoursite.com' instead of 'yoursite.com'",
      }
    ),
  dns_method: z.enum(['CNAME', 'NS']),
});

export type DomainFormData = z.infer<typeof domainSchema>;

export interface DomainFormProps {
  onSubmit: (data: DomainFormData) => Promise<void>;
  onCancel: () => void;
  isLoading?: boolean;
  error?: string | null;
}

export function DomainForm({
  onSubmit,
  onCancel,
  isLoading = false,
  error,
}: DomainFormProps) {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<DomainFormData>({
    resolver: zodResolver(domainSchema),
    defaultValues: {
      dns_method: 'CNAME',
    },
  });

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="domain-form">
      {error && (
        <div className="domain-form__error alert alert--error">
          <p>{error}</p>
        </div>
      )}

      <div className="domain-form__field">
        <label htmlFor="domain_name" className="label">
          Domain Name
        </label>
        <input
          id="domain_name"
          type="text"
          {...register('domain_name')}
          className={errors.domain_name ? 'input input--error' : 'input'}
          placeholder="go.example.com"
          disabled={isLoading}
          aria-invalid={errors.domain_name ? 'true' : 'false'}
          aria-describedby={errors.domain_name ? 'domain_name-error' : 'domain_name-help'}
        />
        {errors.domain_name ? (
          <p id="domain_name-error" className="domain-form__error-text" role="alert">
            {errors.domain_name.message}
          </p>
        ) : (
          <p id="domain_name-help" className="domain-form__help-text">
            Use a subdomain like 'go.yoursite.com'. Root domains are not supported.
          </p>
        )}
      </div>

      <div className="domain-form__field">
        <label htmlFor="dns_method" className="label">
          DNS Method
        </label>
        <select
          id="dns_method"
          {...register('dns_method')}
          className="select"
          disabled={isLoading}
        >
          <option value="CNAME">
            CNAME (Recommended)
          </option>
          <option value="NS">NS (Delegation)</option>
        </select>
        <p className="domain-form__help-text">
          CNAME is recommended for most users. NS delegation gives Origyns full control.
        </p>
      </div>

      <div className="domain-form__actions">
        <Button
          type="button"
          variant="secondary"
          onClick={onCancel}
          disabled={isLoading}
        >
          Cancel
        </Button>
        <Button type="submit" disabled={isLoading}>
          {isLoading ? 'Adding...' : 'Add Domain'}
        </Button>
      </div>
    </form>
  );
}

